// ---------------------------------------------------------------------------
// entity-resolver.ts — Resolves short entity IDs (@id:task_001 etc.) to
// human-readable display names throughout text and report objects.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IdMap = Record<string, string>;
export type DisplayNameMaps = Record<string, string>;

export interface IdMapSource {
  taskIdMap?: IdMap;
  groupIdMap?: IdMap;
  resourceIdMap?: IdMap;
  milestoneIdMap?: IdMap;
  meetingIdMap?: IdMap;
}

export interface RawReport {
  executiveBrief?: string;
  overallAssessment?: string;
  risks?: RawReportRisk[];
}

interface RawReportRisk {
  title?: string;
  description?: string;
  mitigation?: string;
  affectedTasks?: string[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Merge short-ID → display-name entries from one or more sources into a
 * single flat lookup. Later sources override earlier ones for the same key.
 */
export function buildDisplayNameMaps(...sources: (IdMapSource | null | undefined)[]): DisplayNameMaps {
  const maps: DisplayNameMaps = Object.create(null);
  for (const source of sources) {
    if (!source) continue;
    addIdMapEntries(maps, source.taskIdMap);
    addIdMapEntries(maps, source.groupIdMap);
    addIdMapEntries(maps, source.resourceIdMap);
    addIdMapEntries(maps, source.milestoneIdMap);
    addIdMapEntries(maps, source.meetingIdMap);
  }
  return maps;
}

/**
 * Replace all short entity IDs in `text` with their display names.
 * Handles three ID formats:
 *   @id:task_001        (canonical)
 *   __task_001__         (double-underscore wrapper)
 *   task_001             (bare)
 */
export function resolveEntityIds(text: string | undefined, idMaps: DisplayNameMaps | null): string {
  if (!text) return '';
  if (!idMaps) return text;

  return String(text).replace(
    /(@id:(?:task|group|res|ms|meeting)_\d{3}|__(?:task|group|res|ms|meeting)_\d{3}__|\b(?:task|group|res|ms|meeting)_\d{3}\b)/g,
    match => {
      const canonical = normalizeEntityId(match);
      return (canonical && idMaps[canonical]) || match;
    },
  );
}

/**
 * Deep-resolve entity IDs across all text fields in a risk report object.
 * Returns a new object — does not mutate the input.
 */
export function resolveReportEntityIds(report: RawReport | null, idMaps: DisplayNameMaps | null): RawReport | null {
  if (!report) return report;

  return {
    ...report,
    executiveBrief: resolveEntityIds(report.executiveBrief || '', idMaps),
    overallAssessment: resolveEntityIds(report.overallAssessment || '', idMaps),
    risks: Array.isArray(report.risks)
      ? report.risks.map(risk => ({
        ...risk,
        title: resolveEntityIds(risk.title || '', idMaps),
        description: resolveEntityIds(risk.description || '', idMaps),
        mitigation: resolveEntityIds(risk.mitigation || '', idMaps),
        affectedTasks: Array.isArray(risk.affectedTasks)
          ? risk.affectedTasks.map(item => resolveEntityIds(item, idMaps))
          : risk.affectedTasks,
      }))
      : [],
  };
}

/**
 * Extract executive brief text from either the structured report field or
 * by parsing the "## Executive Brief" section from analysis markdown.
 */
export function resolveExecutiveBriefText(
  report: RawReport | null | undefined,
  analysisText: string | undefined,
): string {
  const structuredBrief = report && report.executiveBrief ? String(report.executiveBrief).trim() : '';
  if (structuredBrief) return structuredBrief;

  const analysis = String(analysisText || '').replace(/\r\n/g, '\n');
  if (!analysis) return '';

  const lines = analysis.split('\n');
  const collected: string[] = [];
  let inSection = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    const headingMatch = line.match(/^#{1,6}\s*(.+)$/);
    if (headingMatch) {
      const heading = String(headingMatch[1] || '').trim().toLowerCase().replace(/\s*\((?:non-technical|technical)\)\s*$/i, '');
      if (inSection) break;
      if (heading === 'executive brief') {
        inSection = true;
        continue;
      }
    }

    if (inSection) {
      if (line) {
        collected.push(line);
      } else if (collected.length) {
        collected.push('');
      }
    }
  }

  return collected.join(' ').replace(/\s{2,}/g, ' ').trim();
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function addIdMapEntries(target: DisplayNameMaps, source: IdMap | undefined): void {
  if (!source) return;
  for (const key of Object.keys(source)) {
    if (typeof source[key] === 'string' && source[key]) {
      target[key] = source[key];
    }
  }
}

function normalizeEntityId(value: string): string | null {
  if (!value) return null;
  if (value.startsWith('@id:')) return value;
  if (value.startsWith('__') && value.endsWith('__')) {
    return '@id:' + value.slice(2, -2);
  }
  if (/^(?:task|group|res|ms|meeting)_\d{3}$/.test(value)) {
    return '@id:' + value;
  }
  return null;
}
